import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2, Link2 } from 'lucide-react';
import { authAxios } from '@/lib/axios';
import { linkConfirmErrorKey } from '@/lib/linkConfirmError';
import { useAuth } from '@/contexts/AuthContext';

type Status = 'idle' | 'processing' | 'success' | 'error';

const LinkAccount = () => {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>('idle');
  const [errorKey, setErrorKey] = useState<string>('linkAccount.error.generic');

  const handleConfirm = async () => {
    if (!code) {
      setErrorKey('linkAccount.error.missingCode');
      setStatus('error');
      return;
    }
    setStatus('processing');
    try {
      await authAxios.post('/api/v1/link/confirm', { code });
      setStatus('success');
    } catch (error) {
      setErrorKey(linkConfirmErrorKey(error));
      setStatus('error');
    }
  };

  const identityPanel = user && (
    <div className="rounded-lg bg-gray-900/60 border border-gray-700 px-4 py-3 mb-4 text-center">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
        {t('linkAccount.connectingAs')}
      </p>
      {user.name && <p className="text-gray-100 font-medium">{user.name}</p>}
      <p className="text-gray-400 text-sm">{user.email}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        {status === 'success' && (
          <>
            <CardHeader className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <CardTitle className="text-gray-100">{t('linkAccount.successTitle')}</CardTitle>
              <CardDescription className="text-gray-400">
                {t('linkAccount.successDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>{identityPanel}</CardContent>
          </>
        )}

        {status === 'error' && (
          <>
            <CardHeader className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
              <CardTitle className="text-gray-100">{t('linkAccount.errorTitle')}</CardTitle>
              <CardDescription className="text-gray-400">{t(errorKey)}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => setStatus('idle')}>
                {t('linkAccount.tryAgain')}
              </Button>
            </CardContent>
          </>
        )}

        {(status === 'idle' || status === 'processing') && (
          <>
            <CardHeader className="text-center">
              <Link2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <CardTitle className="text-gray-100">{t('linkAccount.title')}</CardTitle>
              <CardDescription className="text-gray-400">
                {t('linkAccount.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {identityPanel}
              <Button
                className="w-full"
                onClick={handleConfirm}
                disabled={status === 'processing'}
              >
                {status === 'processing' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('linkAccount.connecting')}
                  </>
                ) : (
                  t('linkAccount.confirmButton')
                )}
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default LinkAccount;
